import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import {
  archiveStay,
  deleteStay,
  ReservationStaleError,
  setStayAccess,
  transitionStay,
  unarchiveStay,
} from '../../../../api/stays-reservations-client';
import type { StayFull, StayStatus } from '../../../../interfaces/stays';
import { AppButton } from '../../../button/app-button';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import { useMenuDismiss } from '../../../hooks/use-menu-dismiss';
import { m, overlayMotion, Presence } from '../../../../motion';
import toast from '../../../../utils/toast';
import { touch } from '../../../../style-utils/media-queries';
import {
  layout,
  palette,
  radius,
  shadow,
  space,
  spacing,
  status as statusTone,
  transition,
  typography,
  zIndex,
} from '../../../../style-utils/theme';
import { refusalText } from '../../dashboard/dashboard-stays-components/stays-labels';
import { stayDates } from '../../../../utils/stays-stage';
import { firstOf, fullName } from './reservation-labels';
import { useDeskInPlan } from '../../../hooks/use-desk-in-plan';

/**
 * ONE STAY'S "MORE" MENU (plan S4, R-S4.24): the status moves, guest access,
 * archive and delete — each item present ONLY when the stay allows it (absent,
 * not disabled: a menu of greyed-out verbs is a list of things you cannot do).
 * The server is the gate; these rules only decide what to offer, and every
 * call carries the row's `version`.
 *
 * Cancel, Delete and Revoke ask first (ConfirmDialog, destructive, the safe
 * option focused). A stale version answers STAY_CHANGED with the current stay:
 * the menu says so and hands the fresh stay up (`onChanged`), and the caller
 * reads its list again. Shared with the calendar (S4.10).
 */

export type StayMenuKey =
  | 'check_in'
  | 'check_out'
  | 'undo_check_in'
  | 'undo_check_out'
  | 'no_show'
  | 'attended'
  | 'cancel'
  | 'restore'
  | 'revoke'
  | 'restore_access'
  | 'unarchive'
  | 'archive'
  | 'delete';

const LABEL: Record<StayMenuKey, string> = {
  check_in: 'Mark checked in',
  check_out: 'Mark checked out',
  undo_check_in: 'Undo check-in',
  undo_check_out: 'Undo check-out',
  no_show: 'Mark no-show',
  attended: 'Mark attended',
  cancel: 'Cancel stay',
  restore: 'Restore stay',
  revoke: 'Revoke guest access',
  restore_access: 'Restore guest access',
  unarchive: 'Undo archive',
  archive: 'Archive',
  delete: 'Delete',
};

const DESTRUCTIVE = new Set<StayMenuKey>(['cancel', 'revoke', 'delete']);

type MenuStay = Pick<
  StayFull,
  'status' | 'stage' | 'archived_at' | 'access_revoked' | 'check_out' | 'deletable'
>;

/** What the menu offers for a stay on `buildingToday` (a building day key). */
export const stayMenuItems = (stay: MenuStay, buildingToday: string): { key: StayMenuKey; label: string }[] => {
  const keys: StayMenuKey[] = [];
  const archived = !!stay.archived_at;
  const s = stay.status;
  if (!archived) {
    if (s === 'confirmed' && (stay.stage.key === 'arrival_day' || stay.stage.key === 'in_house')) keys.push('check_in');
    if (s === 'checked_in') keys.push('check_out', 'undo_check_in');
    if (s === 'checked_out') keys.push('undo_check_out');
    if (s === 'confirmed' && stay.stage.days_until <= 0) keys.push('no_show');
    if (s === 'no_show') keys.push('attended');
    if (s === 'confirmed') keys.push('cancel');
    if (s === 'cancelled') keys.push('restore');
    keys.push(stay.access_revoked ? 'restore_access' : 'revoke');
    // Day keys compare as strings ('YYYY-MM-DD'): no Date is ever built.
    const over = s === 'cancelled' || s === 'no_show' || s === 'checked_out' || stay.check_out <= buildingToday;
    if (over) keys.push('archive');
  } else {
    keys.push('unarchive');
  }
  if (stay.deletable) keys.push('delete');

  return keys.map(key => ({ key, label: LABEL[key] }));
};

const MOVE: Partial<Record<StayMenuKey, StayStatus>> = {
  check_in: 'checked_in',
  check_out: 'checked_out',
  undo_check_in: 'confirmed',
  undo_check_out: 'checked_in',
  no_show: 'no_show',
  attended: 'confirmed',
  cancel: 'cancelled',
  restore: 'confirmed',
};

const doneText = (key: StayMenuKey, first: string): string => {
  switch (key) {
    case 'check_in':
      return `${first} is checked in.`;
    case 'check_out':
      return `${first} is checked out.`;
    case 'undo_check_in':
      return `${first}'s check-in is undone.`;
    case 'undo_check_out':
      return `${first}'s check-out is undone.`;
    case 'no_show':
      return `${first}'s stay is marked as a no-show.`;
    case 'attended':
      return `${first}'s stay is marked as attended.`;
    case 'cancel':
      return `${first}'s stay is cancelled.`;
    case 'restore':
      return `${first}'s stay is restored.`;
    case 'revoke':
      return `${first}'s guest access is revoked.`;
    case 'restore_access':
      return `${first}'s guest access is restored.`;
    case 'unarchive':
      return `${first}'s stay is back from the archive.`;
    case 'archive':
      return `${first}'s stay archived`;
    case 'delete':
    default:
      return `${first}'s stay is deleted.`;
  }
};

export const STALE_TEXT = 'This stay changed since you opened it. It has been read again.';

/**
 * The one way a stay write fails on screen: a stale version says so (and the
 * caller reads again); any other refusal shows the server's Stays words.
 * Returns the server's current stay on a stale refusal, when it sent one.
 */
export const reportStayError = (error: unknown, generic: string): StayFull | null => {
  if (error instanceof ReservationStaleError) {
    toast.error(STALE_TEXT);

    return error.current;
  }
  toast.error(refusalText(error as { code?: string | null; message?: string }, generic));

  return null;
};

const Anchor = styled.span`
  position: relative;
  display: inline-flex;
  min-width: 0;
`;

const Menu = m.div`
  position: absolute;
  top: calc(100% + ${spacing.half});
  right: 0;
  z-index: ${zIndex.dropdown};
  display: flex;
  flex-direction: column;
  min-width: ${space(28)};
  max-width: calc(100vw - ${spacing.lg});
  padding: ${spacing.half};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.md};
`;

const Item = styled.button<{ $danger: boolean }>`
  -webkit-appearance: none;
  appearance: none;
  display: flex;
  align-items: center;
  width: 100%;
  min-height: ${layout.compactControlHeight};
  padding: ${spacing.xs} ${spacing.sm};
  border: 0;
  border-radius: ${radius.xs};
  background: transparent;
  color: ${({ $danger }) => ($danger ? statusTone.alert.fg : palette.ink)};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.medium};
  text-align: left;
  white-space: nowrap;
  cursor: pointer;
  transition: ${transition.colors};

  &:hover,
  &:focus-visible {
    outline: none;
    background: ${({ $danger }) => ($danger ? palette.alertSoft : palette.tealTintSoft)};
  }

  &:focus-visible {
    box-shadow: ${shadow.focus};
  }

  ${touch} {
    min-height: ${layout.touchTarget};
  }
`;

export interface ReservationActionsMenuProps {
  stay: StayFull;
  /** The envelope's building day — the Archive rule reads it. */
  buildingToday: string;
  /** After a write: the stay as it now is, or null once it is deleted. */
  onChanged: (next: StayFull | null) => void;
  /** After an archive, for the caller's "Undo archive" control. */
  onArchived?: (stay: StayFull) => void;
}

export const ReservationActionsMenu: React.FC<ReservationActionsMenuProps> = ({
  stay,
  buildingToday,
  onChanged,
  onArchived,
}) => {
  // Independent Stays IS1: no desk to warn in an organisation without Desk.
  const desk = useDeskInPlan();
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState<StayMenuKey | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const inFlight = useRef(false);
  const items = stayMenuItems(stay, buildingToday);
  const name = fullName(stay);
  const first = firstOf(stay);

  const close = useCallback((refocus = true) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  }, []);
  const dismiss = useCallback(() => close(true), [close]);
  useMenuDismiss(open, dismiss, { menu: menuRef, trigger: triggerRef });

  // The first item takes the focus when the menu opens.
  useEffect(() => {
    if (!open) return;
    const firstItem = menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]');
    firstItem?.focus();
  }, [open]);

  const onMenuKey = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const list = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') || []);
    if (!list.length) return;
    const at = list.indexOf(document.activeElement as HTMLButtonElement);
    let next = -1;
    if (event.key === 'ArrowDown') next = at < 0 ? 0 : (at + 1) % list.length;
    else if (event.key === 'ArrowUp') next = at <= 0 ? list.length - 1 : at - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = list.length - 1;
    else if (event.key === 'Tab') {
      close(false);

      return;
    }
    if (next >= 0) {
      event.preventDefault();
      list[next].focus();
    }
  };

  const run = async (key: StayMenuKey) => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const to = MOVE[key];
      if (to) {
        const next = await transitionStay(stay.id, to, stay.version);
        toast.success(doneText(key, first));
        onChanged(next);
      } else if (key === 'revoke' || key === 'restore_access') {
        const next = await setStayAccess(stay.id, key === 'revoke', stay.version);
        toast.success(doneText(key, first));
        onChanged(next);
      } else if (key === 'archive') {
        const next = await archiveStay(stay.id, stay.version);
        toast.success(doneText(key, first));
        onChanged(next);
        if (onArchived) onArchived(next);
      } else if (key === 'unarchive') {
        const next = await unarchiveStay(stay.id, stay.version);
        toast.success(doneText(key, first));
        onChanged(next);
      } else if (key === 'delete') {
        await deleteStay(stay.id, stay.version);
        toast.success(doneText(key, first));
        onChanged(null);
      }
    } catch (error) {
      const current = reportStayError(error, 'Could not change the stay. Try again.');
      // The caller reads its list again on any change; a stale refusal hands
      // it the server's current stay when one came back.
      onChanged(current || stay);
    } finally {
      inFlight.current = false;
    }
  };

  const choose = (key: StayMenuKey) => {
    close(true);
    if (DESTRUCTIVE.has(key)) setConfirming(key);
    else void run(key);
  };

  if (!items.length) return null;

  const confirmCopy =
    confirming === 'cancel'
      ? {
          title: `Cancel ${first}'s stay?`,
          message: `${stayDates(stay.check_in, stay.check_out)}. The check-in link stops working.`,
          primary: `Cancel ${first}'s stay`,
          dismiss: 'Keep stay',
        }
      : confirming === 'delete'
      ? {
          title: `Delete ${first}'s stay?`,
          message: 'The stay is removed for good. Archive it instead to keep it in your records.',
          primary: `Delete ${first}'s stay`,
          dismiss: 'Keep it',
        }
      : {
          title: `Revoke ${first}'s guest access?`,
          message: desk
            ? 'Their check-in link stops working, and the desk is told to check with you before letting them in.'
            : 'Their check-in link stops working.',
          primary: `Revoke ${first}'s guest access`,
          dismiss: 'Keep access',
        };

  return (
    <Anchor>
      <AppButton
        ref={triggerRef}
        variant="ghost"
        size="compact"
        aria-label={`More actions for ${name}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >
        More
      </AppButton>
      <Presence>
        {open ? (
          <Menu
            key="menu"
            ref={menuRef}
            role="menu"
            aria-label={`Actions for ${name}`}
            onKeyDown={onMenuKey}
            {...overlayMotion('popover')}
            style={{ transformOrigin: 'top right' }}
          >
            {items.map(item => (
              <Item
                key={item.key}
                type="button"
                role="menuitem"
                tabIndex={-1}
                data-action={item.key}
                $danger={DESTRUCTIVE.has(item.key)}
                onClick={() => choose(item.key)}
              >
                {item.label}
              </Item>
            ))}
          </Menu>
        ) : null}
      </Presence>
      <ConfirmDialog
        open={!!confirming}
        title={confirmCopy.title}
        message={confirmCopy.message}
        primaryLabel={confirmCopy.primary}
        primaryVariant="destructive"
        dismissLabel={confirmCopy.dismiss}
        onPrimary={async () => {
          if (confirming) await run(confirming);
          setConfirming(null);
        }}
        onDismiss={() => setConfirming(null)}
      />
    </Anchor>
  );
};
